// components/ModalEmpleados.js
import React, { useState, useMemo, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Typography,
  Box,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from "@mui/material"
import { Search, Close, ArrowBack, Delete } from "@mui/icons-material"

const ModalEmpleados = ({ open, onClose, empleados, eventos, onDeleteEvents, loading }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null)
  const [eventosSeleccionados, setEventosSeleccionados] = useState({})
  const [busquedaEventos, setBusquedaEventos] = useState("")

  // Filtrar empleados por nombre o ID
  const empleadosFiltrados = useMemo(
    () =>
      empleados.filter(
        (empleado) =>
          empleado.emnombre.toLowerCase().includes(searchTerm.toLowerCase()) || empleado.emcodemp.includes(searchTerm),
      ),
    [empleados, searchTerm],
  )

  // Obtener eventos por empleado
  const getEventosPorEmpleado = useMemo(() => {
    const eventosPorEmpleado = {}
    eventos.forEach((evento) => {
      const emcodemp = evento.extendedProps.emcodemp
      if (!eventosPorEmpleado[emcodemp]) {
        eventosPorEmpleado[emcodemp] = []
      }
      eventosPorEmpleado[emcodemp].push(evento)
    })
    return eventosPorEmpleado
  }, [eventos])

  // Filtrar eventos del empleado seleccionado
  const eventosFiltrados = useMemo(() => {
    if (!empleadoSeleccionado) return []

    const eventosEmpleado = getEventosPorEmpleado[empleadoSeleccionado.emcodemp] || []

    if (!busquedaEventos) return eventosEmpleado

    return eventosEmpleado.filter(
      (evento) =>
        evento.extendedProps.localidadNombre.toLowerCase().includes(busquedaEventos.toLowerCase()) ||
        evento.extendedProps.horarioDesc.toLowerCase().includes(busquedaEventos.toLowerCase()) ||
        evento.extendedProps.plandescri?.toLowerCase().includes(busquedaEventos.toLowerCase()) ||
        new Date(evento.start).toLocaleDateString().includes(busquedaEventos),
    )
  }, [empleadoSeleccionado, getEventosPorEmpleado, busquedaEventos])

  // Verificar si todos los eventos están seleccionados
  const todosSeleccionados = useMemo(() => {
    if (!empleadoSeleccionado || eventosFiltrados.length === 0) return false
    return eventosFiltrados.every((evento) => eventosSeleccionados[evento.id])
  }, [eventosFiltrados, eventosSeleccionados, empleadoSeleccionado])

  // Contador de eventos seleccionados
  const eventosSeleccionadosCount = useMemo(
    () => Object.values(eventosSeleccionados).filter((selected) => selected).length,
    [eventosSeleccionados],
  )

  const handleEmpleadoClick = (empleado) => {
    setEmpleadoSeleccionado(empleado)
    setEventosSeleccionados({})
    setBusquedaEventos("")
  }

  const handleBackToList = () => {
    setEmpleadoSeleccionado(null)
    setEventosSeleccionados({})
    setBusquedaEventos("")
  }

  const handleSeleccionarEvento = (eventoId) => (event) => {
    setEventosSeleccionados((prev) => ({
      ...prev,
      [eventoId]: event.target.checked,
    }))
  }

  const handleSeleccionarTodos = (event) => {
    const nuevosSeleccionados = {}
    eventosFiltrados.forEach((evento) => {
      nuevosSeleccionados[evento.id] = event.target.checked
    })
    setEventosSeleccionados(nuevosSeleccionados)
  }

  const handleEliminarSeleccionados = async () => {
    if (eventosSeleccionadosCount === 0) return

    const eventosAEliminar = eventosFiltrados
      .filter((evento) => eventosSeleccionados[evento.id])
      .map((evento) => evento.extendedProps)

    try {
      await onDeleteEvents(eventosAEliminar)
      // Limpiar selección después de eliminar
      setEventosSeleccionados({})
    } catch (error) {
      console.error("Error al eliminar eventos:", error)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  useEffect(() => {
    if (!open) {
      // Se cerró el modal, reseteamos todo
      setEmpleadoSeleccionado(null)
      setEventosSeleccionados({})
      setBusquedaEventos("")
      setSearchTerm("")
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {empleadoSeleccionado ? (
            <>
              <IconButton onClick={handleBackToList} size="small">
                <ArrowBack />
              </IconButton>
              <Typography variant="h6">{empleadoSeleccionado.emnombre}</Typography>
              <div style={{ width: 48 }} />
            </>
          ) : (
            <>
              <Typography variant="h6">Lista de Empleados</Typography>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {!empleadoSeleccionado ? (
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar empleado por nombre o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{ mb: 2 }}
            />

            <List>
              {empleadosFiltrados.map((empleado) => (
                <React.Fragment key={empleado.emcodemp}>
                  <ListItem
                    button
                    onClick={() => handleEmpleadoClick(empleado)}
                    sx={{
                      "&:hover": { backgroundColor: "action.hover" },
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">{empleado.emnombre}</Typography>
                          <Chip label={`ID: ${empleado.emcodemp}`} size="small" color="primary" variant="outlined" />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="textSecondary">
                          {getEventosPorEmpleado[empleado.emcodemp]?.length || 0} tareas programadas
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={getEventosPorEmpleado[empleado.emcodemp]?.length || 0}
                        color="primary"
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>

            {empleadosFiltrados.length === 0 && (
              <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>
                No se encontraron empleados
              </Typography>
            )}
          </>
        ) : (
          <Box>
            {/* Barra de búsqueda de eventos */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar eventos por localidad, horario o descripción..."
              value={busquedaEventos}
              onChange={(e) => setBusquedaEventos(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{ mb: 2 }}
            />

            {/* Controles de selección múltiple */}
            {eventosFiltrados.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={todosSeleccionados}
                      onChange={handleSeleccionarTodos}
                      indeterminate={eventosSeleccionadosCount > 0 && !todosSeleccionados}
                    />
                  }
                  label={`Seleccionar todos (${eventosSeleccionadosCount})`}
                />
              </Box>
            )}

            {loading && (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Total de eventos: {eventosFiltrados.length}
              </Typography>

              {eventosFiltrados.length > 0 ? (
                <List>
                  {eventosFiltrados.map((evento, index) => (
                    <React.Fragment key={evento.id}>
                      <ListItem
                        sx={{
                          flexDirection: "column",
                          alignItems: "flex-start",
                          backgroundColor: eventosSeleccionados[evento.id] ? "action.selected" : "transparent",
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" width="100%">
                          <Checkbox
                            checked={!!eventosSeleccionados[evento.id]}
                            onChange={handleSeleccionarEvento(evento.id)}
                            sx={{ mr: 1, mt: 0.5 }}
                          />

                          <Box flex={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                              <Box>
                                <Typography variant="subtitle2">{formatDate(evento.start)}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {evento.extendedProps.localidadNombre}
                                </Typography>
                              </Box>
                              <Chip
                                label={evento.extendedProps.horarioDesc}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </Box>

                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2">
                                {formatTime(evento.start)} - {formatTime(evento.end)}
                              </Typography>
                            </Box>

                            {evento.extendedProps.plandescri && (
                              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                {evento.extendedProps.plandescri}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                      {index < eventosFiltrados.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>
                  {busquedaEventos
                    ? "No se encontraron eventos que coincidan con la búsqueda"
                    : "No hay tareas programadas para este empleado"}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={empleadoSeleccionado ? handleBackToList : onClose}>
          {empleadoSeleccionado ? "Volver a la lista" : "Cerrar"}
        </Button>

        {empleadoSeleccionado && eventosSeleccionadosCount > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleEliminarSeleccionados}
            disabled={loading}
            startIcon={<Delete />}
          >
            Eliminar ({eventosSeleccionadosCount})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ModalEmpleados
